import type { CartLine } from '../types/cart'
import type { Product } from '../types/catalog'
import type { OrderLineVia } from '../types/requests'

/**
 * Een protocol als bundel in de winkelmand. De mand blijft regels per product (de server prijst
 * per product, er is geen bundelkorting: de leverancier staat dat niet toe). Een bundel is de
 * groep regels met hetzelfde `routineId` en `bundle: true`. Alles hier is puur en getest in
 * core/rules/cartBundles.test.ts.
 */

export type CartGroup = { kind: 'bundle'; routineId: string; lines: CartLine[] } | { kind: 'line'; line: CartLine }

export const inBundle = (l: CartLine, routineId?: string): boolean =>
  Boolean(l.bundle && l.routineId && (routineId === undefined || l.routineId === routineId))

/** De mand in blokken: elke bundel staat op de plek van zijn eerste regel. */
export function groupCart(lines: readonly CartLine[]): CartGroup[] {
  const groups: CartGroup[] = []
  const bundles = new Map<string, CartLine[]>()
  for (const l of lines) {
    if (!inBundle(l)) {
      groups.push({ kind: 'line', line: l })
      continue
    }
    const id = l.routineId as string
    const existing = bundles.get(id)
    if (existing) {
      existing.push(l)
      continue
    }
    const group: CartLine[] = [l]
    bundles.set(id, group)
    groups.push({ kind: 'bundle', routineId: id, lines: group })
  }
  return groups
}

/** De bundel valt uiteen: de regels blijven, als gewone regels (het protocol-id blijft voor Inzicht). */
export function splitBundle(lines: CartLine[], routineId: string): CartLine[] {
  return lines.map((l) => (inBundle(l, routineId) ? { ...l, bundle: false } : l))
}

/**
 * Eén product eruit. Zat het in een bundel, dan is de rest geen compleet protocol meer en wordt
 * het gewone regels. `split` = welke producten daardoor uit de bundel gingen (voor "Ongedaan maken").
 */
export function withoutProduct(lines: CartLine[], productId: string): { lines: CartLine[]; split: { routineId: string; productIds: string[] } | null } {
  const gone = lines.find((l) => l.product.id === productId)
  const rest = lines.filter((l) => l.product.id !== productId)
  if (!gone || !inBundle(gone)) return { lines: rest, split: null }
  const routineId = gone.routineId as string
  const productIds = rest.filter((l) => inBundle(l, routineId)).map((l) => l.product.id)
  return { lines: splitBundle(rest, routineId), split: productIds.length ? { routineId, productIds } : null }
}

export type RemovedLine = { line: CartLine; at: number }

/** De hele bundel eruit, met de oude plekken (voor "Ongedaan maken"). */
export function withoutBundle(lines: CartLine[], routineId: string): { lines: CartLine[]; removed: RemovedLine[] } {
  const removed = lines.flatMap((line, at) => (inBundle(line, routineId) ? [{ line, at }] : []))
  return { lines: lines.filter((l) => !inBundle(l, routineId)), removed }
}

/** Regels terugzetten op hun oude plek. Wat intussen opnieuw in de mand kwam, blijft zoals het is. */
export function restoreLines(lines: CartLine[], removed: readonly RemovedLine[]): CartLine[] {
  const next = [...lines]
  for (const r of [...removed].sort((a, b) => a.at - b.at)) {
    if (next.some((l) => l.product.id === r.line.product.id)) continue
    next.splice(Math.min(r.at, next.length), 0, r.line)
  }
  return next
}

/** Eén keuze voor de hele bundel. Een product dat niet bijvulbaar is, blijft eenmalig. */
export function withBundleSubscribe(lines: CartLine[], routineId: string, subscribe: boolean, canRefill: (p: Product) => boolean): CartLine[] {
  return lines.map((l) => (inBundle(l, routineId) ? { ...l, subscribe: subscribe && canRefill(l.product) } : l))
}

/**
 * Een protocol als bundel erbij. Staat een product al in de mand, dan gaat die regel de bundel in
 * en houdt hij zijn aantal; anders komt er een nieuwe regel bij (aantal 1). Uitverkochte producten
 * laat de aanroeper eerst weg.
 */
export function withRoutineBundle(
  lines: CartLine[],
  routineId: string,
  products: readonly Product[],
  opts: { subscribe: boolean; via?: OrderLineVia; canRefill: (p: Product) => boolean },
): CartLine[] {
  const next = [...lines]
  for (const product of products) {
    const subscribe = opts.subscribe && opts.canRefill(product)
    const via = opts.via ? { via: opts.via } : {}
    const at = next.findIndex((l) => l.product.id === product.id)
    if (at >= 0) next[at] = { ...next[at], routineId, bundle: true, subscribe, ...via }
    else next.push({ product, qty: 1, subscribe, routineId, bundle: true, ...via })
  }
  return next
}

/** Ontbreekt er een product van het protocol in de bundel (bijv. uitverkocht bij het toevoegen)? */
export const bundleIncomplete = (routineProductIds: readonly string[], bundle: readonly CartLine[]) =>
  routineProductIds.some((id) => !bundle.some((l) => l.product.id === id))

// ——— Ritme ———

export const monthsToWeeks = (months: number) => Math.round((months * 52) / 12)

/** Een gekozen ritme alleen als het een van de toegestane is; anders `null`. */
export const allowedInterval = (intervals: readonly number[], weeks: unknown): number | null =>
  typeof weeks === 'number' && intervals.includes(weeks) ? weeks : null
